import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Eye, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type ListingBuyerLeadsFragment } from '@graphql/__generated__/graphql';
import { BuyerLeadAddModal } from '@modules/_shared/components/buyer-lead/BuyerLeadAddModal';
import { GET_BUYER_LEAD_STAGES } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import {
  type CardHeaderIconAction,
  CardHeaderIconActions,
} from '@modules/_shared/components/CardHeaderIconActions';
import {
  CountActionCard,
  CountActionCardSkeleton,
} from '@modules/_shared/components/CountActionCard';
import {
  SellerNotificationSettings,
  SellerNotificationSettingsSkeleton,
} from '@modules/_shared/components/listing-overview/SellerNotificationSettings';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { GET_LISTING_BUYERS_DASHBOARD } from '@modules/listings/listingBuyersDashboardQueries';
import { GET_LISTING_BUYERS_LEAD } from '@modules/listings/ListingBuyersLead';
import { GET_LISTING_DETAILS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

interface ListingBuyerLeadsCardProps {
  buyerLeadsData?: ListingBuyerLeadsFragment | null;
  listingId?: string | null;
  isLoading?: boolean;
  openedDefault?: boolean;
  readOnly?: boolean;
}

export const LISTING_BUYER_LEADS_FRAGMENT = gql(/* GraphQL */ `
  fragment ListingBuyerLeads on lots {
    ...SellerNotificationSettings
    buyer_leads(
      where: {
        _and: [
          { source: { _in: ["matching", "manual"] } }
          { user: { is_deleted: { _eq: false } } }
        ]
      }
    ) {
      id
      source
    }
    enquiries_aggregate(where: { buyer_lead_id: { _is_null: false } }) {
      aggregate {
        count
      }
    }
  }
`);

export const ListingBuyerLeadsCardSkeleton: React.FC<{
  openedDefault?: boolean;
}> = ({ openedDefault }) => {
  const { t } = useLocale();
  return (
    <ListingAccordion title={t('Buyer leads')} defaultExpanded={openedDefault}>
      <CountActionCardSkeleton
        countLabels={[t('Matches'), t('Enquiries'), t('Other')]}
      >
        <SellerNotificationSettingsSkeleton />
      </CountActionCardSkeleton>
    </ListingAccordion>
  );
};

export const ListingBuyerLeadsCard: React.FC<ListingBuyerLeadsCardProps> = ({
  buyerLeadsData,
  openedDefault = false,
  listingId,
  isLoading = false,
  readOnly = false,
}) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const [addModalOpen, setAddModalOpen] = useState(false);

  const { data: stagesData } = useQuery(GET_BUYER_LEAD_STAGES, {
    skip: readOnly || listingId == null,
  });
  const stages = stagesData?.lead_stages ?? [];

  const matchingLeadsCount = buyerLeadsData?.buyer_leads.filter(
    ({ source }) => source === 'matching',
  ).length;
  const manualLeadsCount = buyerLeadsData?.buyer_leads.filter(
    ({ source }) => source === 'manual',
  ).length;

  const buyersLink =
    listingId != null
      ? { to: `/listings/${listingId}/buyers`, search: searchParams.toString() }
      : undefined;

  const headerActions: CardHeaderIconAction[] =
    readOnly || buyersLink == null
      ? []
      : [
          {
            label: t('Add buyer lead'),
            icon: <Plus size={16} />,
            ...(stages.length > 0
              ? { onClick: () => setAddModalOpen(true) }
              : { link: buyersLink }),
          },
          {
            label: t('View buyer leads'),
            icon: <Eye size={16} />,
            link: buyersLink,
          },
        ];

  return (
    <>
      <ListingAccordion
        title={t('Buyer leads')}
        defaultExpanded={openedDefault}
        headerAction={
          headerActions.length > 0 ? (
            <CardHeaderIconActions actions={headerActions} />
          ) : undefined
        }
      >
        <CountActionCard
          isLoading={isLoading}
          countElements={[
            { label: t('Matches'), value: matchingLeadsCount },
            {
              label: t('Enquiries'),
              value: buyerLeadsData?.enquiries_aggregate.aggregate?.count,
            },
            { label: t('Other'), value: manualLeadsCount },
          ]}
        >
          <SellerNotificationSettings
            listing={buyerLeadsData}
            isLoading={isLoading}
            readOnly={readOnly}
          />
        </CountActionCard>
      </ListingAccordion>

      {listingId != null && stages.length > 0 && (
        <BuyerLeadAddModal
          lotId={listingId}
          stages={stages}
          defaultStageName="interested"
          opened={addModalOpen}
          onClose={() => setAddModalOpen(false)}
          refetchQueries={[
            {
              query: GET_LISTING_DETAILS,
              variables: { id: listingId },
            },
            {
              query: GET_LISTING_BUYERS_LEAD,
              variables: {
                id: listingId,
                includeBuyerLeads: false,
                buyerLeadsWhere: {},
              },
            },
            {
              query: GET_LISTING_BUYERS_DASHBOARD,
              variables: { id: listingId },
            },
          ]}
        />
      )}
    </>
  );
};
